import { useState, useEffect } from "react";
import { createModel } from "hox";
import { TablePaginationConfig } from "antd/es/table/interface";
import { DEFAULT_FIRST_PAGE_NUM, DEFAULT_PAGE_SIZE, defaultMdCmParam } from "@/utils/constants";
import { ISupplierDetail, ISupplierSearchFields, supplierService, ISupplierInsert, ISupplierUpdate } from "./supplierService";
import { globalPrompt } from "@/components/message";
import { useGlobalModel, transPgToPagination } from "@/model/globalModel";
import { convertRes2Blob } from "@/utils/exportFile";
import { ISearchPage } from "@/types/AppTypes";
import moment from 'moment';

export const useSupplierModel = createModel(function () {
    const { user: { shopCode, shopName, userName }, setLoadingInfo } = useGlobalModel(({ user }) => [user]);
    const { sourceApp, branch } = defaultMdCmParam;
    /**
     * 列表
     */
    //列表数据源
    const [dataSource, setDataSource] = useState<ISupplierDetail[]>([]);
    //查询条件
    const [searchParam, setSearchParam] = useState<ISupplierSearchFields>({});
    //分页参数
    const initPgCfg: TablePaginationConfig = { pageSize: DEFAULT_PAGE_SIZE, current: DEFAULT_FIRST_PAGE_NUM }
    const [pgCfg, setPgCfg] = useState<TablePaginationConfig>(initPgCfg);
    //表格多选id值
    const [selectedRowKeys, setSelectedRowKeys] = useState<ISupplierDetail[]>([]);
    //门店共享
    const [shareShop, serShareShop] = useState<boolean>(false);


    //列表查询
    const loadData = async (page: ISearchPage = initPgCfg, searchValue: ISupplierSearchFields = searchParam) => {
        const searchParams = {
            shopCode,
            ...searchValue,
            pageSize: page?.pageSize ?? pgCfg.pageSize ?? DEFAULT_PAGE_SIZE,
            pageNum: (page?.current || page?.pageNum) ?? pgCfg.current ?? DEFAULT_FIRST_PAGE_NUM,
        }
        let { retData } = await supplierService.page(searchParams);
        const { records, ...pg } = retData;
        setPgCfg(transPgToPagination(pg));
        setDataSource(records);
    }
    //设置搜索value
    const updateSearchParam = (updateParam: ISupplierSearchFields) => {
        setSearchParam({ ...searchParam, ...updateParam })
    }

    //重置搜索参数
    const resetSearchParam = () => {
        setSearchParam({});
        setSelectedRowKeys([]);
        loadData(initPgCfg, {});
    }

    /** 
     * 表单详情
     */

    //编辑表单缓存
    const [editFormVal, setEditFormVal] = useState<any>({});

    const initAddFormVal = {
        shopName,
        createUser: userName,
        status: true
    }
    //新增表单缓存
    const [addFormVal, setAddFormVal] = useState<any>(initAddFormVal);
    //表单新增修改
    const updateAddFormVal = (val: object | null) => {
        val ? setAddFormVal({ ...addFormVal, ...val }) : setAddFormVal(initAddFormVal);
    }
    //表单新增修改
    const updateEditFormVal = (val: object | null) => {
        val ? setEditFormVal({ ...editFormVal, ...val }) : setEditFormVal({});
    }
    //获取一条详情数据 Api
    const getSupplierOne = async (id: string) => {
        let { retData } = await supplierService.one(id);
        retData && setEditFormVal({
            ...retData,
            contractEndTime: retData.contractEndTime === '' ? '' : moment(retData.contractEndTime),
            contractStartTime: retData.contractStartTime === '' ? '' : moment(retData.contractStartTime),
            qualifications: retData?.qualifications === '' ? [] : JSON.parse(retData?.qualifications ?? "[]"),
            createTime:retData.createTime === '' ? '' : moment(retData.createTime),
            updateTime:retData.updateTime === '' ? '' : moment(retData.updateTime),
           
        });
    }

    //表单新增 Api
    const insertSupplier = async (val: any) => {
        let data: ISupplierInsert = {
            ...val,
            sourceApp, branch,
            shopCode, shopName,
            orgCode: "组织机构ID",
            orgName: "组织机构名称",
            status: val.status ? "1" : "0",
            createTime:val.createTime instanceof Object ?  val.createTime.format('YYYY-MM-DDTHH:mm:ss') :null,
            updateTime:val.updateTime instanceof Object ?  val.updateTime.format('YYYY-MM-DDTHH:mm:ss') :null,
        };
        let { retData } = await supplierService.insert(data);
        globalPrompt("message", { text: "供应商新增成功", type: "success" })
        return retData;
    }

    //表单编辑 Api
    const editSupplier = async (val: any & { id: string }) => {
        let data: ISupplierInsert = {
            ...val,
            sourceApp, branch,
            shopCode, shopName,
            orgCode: "组织机构ID",
            orgName: "组织机构名称",
            status: val.status ? "1" : "0",
            createTime:val.createTime instanceof Object ?  val.createTime.format('YYYY-MM-DDTHH:mm:ss') :null,
            updateTime:val.updateTime instanceof Object ?  val.updateTime.format('YYYY-MM-DDTHH:mm:ss') :null,
        };
        let { retData } = await supplierService.update(data);
        globalPrompt("message", { text: "供应商编辑成功", type: "success" })
        return retData;
    }


    //导出
    const exportSupplier = async (searchValue: ISupplierSearchFields = searchParam) => {
        setLoadingInfo({ loadingVisible: true, txt: "快速导出中" });
        try {
            const searchParams = {
                shopCode,
                ...searchValue,
                id: selectedRowKeys.map(item => item.id)?.join(),
                pageSize: -1,
                pageNum: 1,
            };

            let result = await supplierService.export(searchParams);
            result && setLoadingInfo({ loadingVisible: false, txt: "" });
            convertRes2Blob(result);
        } catch { setLoadingInfo({ loadingVisible: false, txt: "" }) }
    }

    //下载模板
    const downloadTpl = async () => {
        let result = await supplierService.downloadTpl();
        convertRes2Blob(result);
    }

    //启用 禁用 api
    const disableSupplier = async (id: string, status: number) => {
        let { retData } = await supplierService.disable(id, status);
        loadData()
        globalPrompt("message", { text: "操作成功", type: "success" })
    };
    return {
        /***field***/
        searchParam,
        dataSource,
        selectedRowKeys,
        pgCfg,
        editFormVal,
        addFormVal,
        shareShop, serShareShop,
        /***method***/
        updateAddFormVal,
        updateEditFormVal,
        insertSupplier,
        editSupplier,
        getSupplierOne,
        setDataSource,
        setSelectedRowKeys,
        resetSearchParam,
        updateSearchParam,
        loadData,
        exportSupplier,
        downloadTpl,
        disableSupplier,
    }
});